/* eslint-disable import/no-cycle */
import { Vendor, VendorResponse } from '@models/response/vendor';

import { OrderState } from '@models/ropolo';
import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OrderChangeStatusPayload } from '../models/payload/order';
import { IError } from '../models/response/error';
import { OrdersResponseMessage } from '../models/response/order';
import { changeState } from '../services/order';
import { vendorMe, vendorMeByDomain } from '../services/vendor';
import NUMBER_TABLE_PER_PAGE from '../shared/constants/numberPerPage';
import { createAppAsyncThunk } from '../utils/reduxUtil';

export interface VendorStateInterface {
  orders: OrdersResponseMessage | undefined;
  stats: {
    state: OrderState;
    count: number;
  }[];
  total: number;
  take: number;
  loading: boolean;
  pageNumber: number;
  pageSize: number;
  filter: string | undefined;
  selectedOrderState: string;
  errors: IError | undefined;
  vendor: Vendor | undefined;
}

const initialState: VendorStateInterface = {
  orders: undefined,
  stats: [],
  total: 0,
  take: 10,
  loading: false,
  pageNumber: 1,
  filter: undefined,
  pageSize: NUMBER_TABLE_PER_PAGE,
  selectedOrderState: '',
  errors: undefined,
  vendor: undefined,
};

const getVendor = createAppAsyncThunk<VendorResponse, { networkStatus: boolean }>('vendor/getVendor', async () => {
  const response = await vendorMe();
  return response;
});

const getVendorByDomain = createAppAsyncThunk<VendorResponse, string>('vendor/getVendorByDomain', async (input) => {
  const response = await vendorMeByDomain(input);
  return response;
});

const changeOrderState = createAppAsyncThunk<void, OrderChangeStatusPayload & { networkStatus: boolean }>(
  'vendor/changeOrderState',
  async (input) => {
    const response = await changeState(input, input.networkStatus);
    return response;
  }
);

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    setTake(state, action: PayloadAction<number>) {
      state.take = action.payload;
    },

    increaseTake(state) {
      state.take += 15;
    },
    setPageNumber: (state, action) => ({
      ...state,
      pageNumber: action.payload,
    }),
    setFilter: (state, action) => ({
      ...state,
      filter: action.payload,
    }),
    setSelectedOrderState: (state, action) => ({
      ...state,
      selectedOrderState: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<VendorStateInterface>) => {
    builder.addCase(changeOrderState.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(changeOrderState.fulfilled, (state) => ({ ...state, loading: false }));
    builder.addCase(changeOrderState.rejected, (state) => ({ ...state, loading: false }));

    builder.addCase(getVendor.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getVendor.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      vendor: action?.payload?.data,
    }));
    builder.addCase(getVendor.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getVendorByDomain.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getVendorByDomain.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      vendor: action?.payload?.data,
    }));
    builder.addCase(getVendorByDomain.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default vendorSlice;
export { changeOrderState, getVendor, getVendorByDomain };
